import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, computed, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, RouteConfigLoadEnd, RouteConfigLoadStart, Router, RouterOutlet } from '@angular/router';
import { SubNavItem } from '@foxera/core/interfaces/sideNav.interface';
import { NavBarComponent } from '@foxera/core/components/navbar/navbar.component';
import { ProfileMenuPopoverComponent } from '@foxera/shared/components/profile-menu-popover/profile-menu-popover.component';
import { WorkoutJobsPopoverComponent } from '@foxera/core/components/workout-jobs-popover/workout-jobs-popover.component';
import { InitialsPipe } from '@foxera/core/pipes/initials.pipe';
import { WorkoutJobsStateService } from '@foxera/shared/services/workout-jobs-state.service';
import { PlanDialogService } from '@foxera/shared/services/plan-dialog.service';
import { ClientPlanComponent } from '@foxera/shared/components/client-plan/client-plan.component';
import { TenantService } from '@foxera/feature/tenant/services/tenant.service';
import { CustomToastComponent } from '@foxera/shared/components/custom-toast/custom-toast.component';
import { GlobalLoadingComponent } from '@foxera/shared/components/foxera-loading/foxera-loading.component';
import { FoxeraSubnavComponent } from '@foxera/shared/components/foxera-subnav/foxera-subnav.component';
import { ConfirmationDialogComponent } from '@foxera/shared/components/confirmation-dialog/confirmation-dialog.component';
import { ApplicationRoutes, isFullScreenRoute } from '@foxera/shared/constants/application.routes';
import { ITenant } from '@foxera/shared/interfaces/tenant.interface';
import { IUserInfo } from '@foxera/shared/interfaces/user-info.interface';
import { IWorkoutPlanJob } from '@foxera/shared/interfaces/client.interface';
import { AuthService } from '@foxera/shared/services/auth.service';
import { ResponsiveService } from '@foxera/shared/services/responsive.service';
import { ConfirmationDialogService } from '@foxera/shared/services/confirmation-dialog.service';
import { LocalStorageService } from '@foxera/shared/services/local-storage.service';
import { ServiceWorkerUpdateService } from '@foxera/shared/services/service-worker-update.service';
import { LogoutService } from '@foxera/shared/services/logout.service';
import { PermissionService } from '@foxera/shared/services/permission.service';
import { ProfileService } from '@foxera/shared/services/profile.service';
import { TokenService } from '@foxera/shared/services/token.service';
import { TooltipModule } from 'primeng/tooltip';
import { filter, first } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app-shell.component.html',
  styleUrls: ['./app-shell.component.css'],
  imports: [
    CommonModule,
    RouterOutlet,
    NavBarComponent,
    FoxeraSubnavComponent,
    FormsModule,
    ProfileMenuPopoverComponent,
    CustomToastComponent,
    GlobalLoadingComponent,
    ConfirmationDialogComponent,
    ClientPlanComponent,
    WorkoutJobsPopoverComponent,
    TooltipModule,
    InitialsPipe,
  ],
})
export class AppShellComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(WorkoutJobsPopoverComponent) workoutJobsPopover!: WorkoutJobsPopoverComponent;
  @ViewChild(ProfileMenuPopoverComponent) profileMenuPopover!: ProfileMenuPopoverComponent;

  subNavItems: SubNavItem[] = [];
  activeSubNavItem: SubNavItem | null = null;
  activeSubNavIndex: number = 0;

  userInfo: IUserInfo | null = null;
  isFullScreenRoute = false;
  hasReducedPadding = false;
  isRouteLoading = false;
  private loadingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private loadingStartTime: number = 0;
  private readonly MIN_LOADING_DISPLAY_TIME = 300;

  // Computed full name for profile avatar
  public fullName = computed(() => {
    const profile = this.profileService.profileDetails();
    if (!profile?.firstName || !profile?.lastName) {
      return '';
    }
    return `${profile.firstName} ${profile.lastName}`;
  });

  private readonly reducedPaddingRoutes = [
    '/schedule'
  ];

  constructor(
    private router: Router,
    private authService: AuthService,
    private localStorageService: LocalStorageService,
    private tenantService: TenantService,
    private tokenService: TokenService,
    private cdr: ChangeDetectorRef,
    public confirmationDialogService: ConfirmationDialogService,
    public profileService: ProfileService,
    public workoutJobsStateService: WorkoutJobsStateService,
    public planDialogService: PlanDialogService,
    public permissionService: PermissionService,
    public logoutService: LogoutService,
    private injector: Injector,
    public responsiveService: ResponsiveService,
    _serviceWorkerUpdates: ServiceWorkerUpdateService
  ) {
    // Set up router events subscription in constructor to catch earliest NavigationEnd
    // This ensures we catch the redirect from '' to '/schedule' on initial load
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        this.updateFullScreenRouteState(event.url);
      });
  }

  ngOnInit(): void {
    this.cdr.markForCheck();
    const userInfo: IUserInfo | null = this.tokenService.getStoredUserInfo();
    const tenant: ITenant | null = this.tenantService.getSelectedTenantFromStorage();
    this.userInfo = userInfo;
    if (userInfo?.sub && tenant?.id) {
      this.tenantService.setSelectedTenant(tenant);
    }

    // Initialize workout job tracking once the profile (and thus employee ID) is loaded
    toObservable(this.profileService.profileDetails, { injector: this.injector })
      .pipe(
        filter((profile) => profile !== null),
        first()
      )
      .subscribe((profile) => {
        const currentTenant =
          this.tenantService.getSelectedTenant() ||
          this.tenantService.getSelectedTenantFromStorage();
        if (profile && currentTenant?.id && this.permissionService.canViewClientWorkoutPlans()) {
          this.workoutJobsStateService.initialize(profile.id, currentTenant.id);
        }
      });

    this.tokenService.userInfo$.subscribe((newUserInfo) => {
      const isNewLogin = newUserInfo && !this.userInfo;
      if (isNewLogin) {
        this.cdr.markForCheck();
        this.redirectToScheduleAfterLogin();
      }

      this.userInfo = newUserInfo;
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (this.loadingTimeoutId) {
          clearTimeout(this.loadingTimeoutId);
          this.loadingTimeoutId = null;
        }
        if (this.isRouteLoading) {
          this.isRouteLoading = false;
          this.loadingStartTime = 0;
          this.cdr.markForCheck();
        }
      } else if (event instanceof RouteConfigLoadStart) {
        this.loadingStartTime = Date.now();
        this.isRouteLoading = true;
        this.cdr.markForCheck();
      } else if (event instanceof RouteConfigLoadEnd) {
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        if (this.isRouteLoading) {
          this.hideLoadingWithDelay();
        }
      }
    });
  }

  ngAfterViewInit(): void {
    // Wait for router to stabilize after any initial redirects, then update route state
    // This is more reliable than arbitrary timeouts as it waits for actual navigation completion
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.updateFullScreenRouteState(this.router.url);
      });

  }

  private redirectToScheduleAfterLogin(): void {
    const tenant = this.tenantService.getSelectedTenantFromStorage();

    if (!tenant?.id) {
      return;
    }

    const currentUrl = this.router.url;
    const isRootRoute = currentUrl === '/' || currentUrl === '';
    const isTenantSelectionRoute = currentUrl === `/${ApplicationRoutes.TENANT_SELECTION}`;
    const isNotScheduleRoute = currentUrl !== `/${ApplicationRoutes.SCHEDULE}`;
    const isNotFullScreenRoute = !isFullScreenRoute(currentUrl);

    const shouldRedirect = isRootRoute ||
      isTenantSelectionRoute ||
      (isNotFullScreenRoute && isNotScheduleRoute);

    if (shouldRedirect) {
      setTimeout(() => {
        this.router.navigate([ApplicationRoutes.SCHEDULE], { replaceUrl: true });
      }, 100);
    }
  }

  private updateFullScreenRouteState(url: string): void {
    this.isFullScreenRoute = isFullScreenRoute(url);
    // Check if URL matches schedule route exactly or starts with /schedule (but not /schedule-shifts)
    const normalizedUrl = url.split('?')[0]; // Remove query params for matching
    this.hasReducedPadding = this.reducedPaddingRoutes.some(route => {
      // Match exact route, route with trailing slash, or route followed by path segment
      // But exclude /schedule-shifts
      const isExactMatch = normalizedUrl === route;
      const startsWithRoute = normalizedUrl.startsWith(route + '/');
      const isScheduleRoute = isExactMatch || startsWithRoute;
      const isNotScheduleShifts = !normalizedUrl.includes('/schedule-shifts');
      return isScheduleRoute && isNotScheduleShifts;
    });
    this.cdr.markForCheck();
  }

  onSubNavChange(subNavItems: SubNavItem[]): void {
    this.subNavItems = subNavItems;
  }

  onActiveSubNavChange(activeItem: SubNavItem | null): void {
    this.activeSubNavItem = activeItem;
  }

  onActiveSubNavIndexChange(index: number): void {
    this.activeSubNavIndex = index;
  }

  private hideLoadingWithDelay(): void {
    if (!this.isRouteLoading) {
      return;
    }

    const elapsed = Date.now() - this.loadingStartTime;
    const remainingTime = Math.max(0, this.MIN_LOADING_DISPLAY_TIME - elapsed);

    if (this.loadingTimeoutId) {
      clearTimeout(this.loadingTimeoutId);
    }

    this.loadingTimeoutId = setTimeout(() => {
      this.isRouteLoading = false;
      this.cdr.markForCheck();
      this.loadingTimeoutId = null;
      this.loadingStartTime = 0;
    }, remainingTime);
  }

  ngOnDestroy(): void {
    if (this.loadingTimeoutId) {
      clearTimeout(this.loadingTimeoutId);
      this.loadingTimeoutId = null;
    }
  }

  logout(): void {
    this.logoutService.logout();
  }

  // ── Workout Jobs Popover ────────────────────────────────────────────────

  openWorkoutJobsOverlay(event: MouseEvent): void {
    this.workoutJobsPopover?.toggle(event);
  }

  onJobClick(job: IWorkoutPlanJob): void {
    this.workoutJobsStateService.navigateToJob(job);
  }

  onCancelJob(job: IWorkoutPlanJob): void {
    this.workoutJobsStateService.cancelJob(job.jobId);
  }

  onRetryJob(job: IWorkoutPlanJob): void {
    this.workoutJobsStateService.retryJob(job.jobId);
  }

  // ── Profile Menu Popover ─────────────────────────────────────────────────

  openProfileMenuOverlay(event: MouseEvent): void {
    this.profileMenuPopover?.toggle(event);
  }

  // ── Plan Dialog ──────────────────────────────────────────────────────────

  onPlanDialogClose(): void {
    this.planDialogService.closePlanDialog();
  }

  onPlanSaved(): void {
    this.planDialogService.notifyPlanSaved();
  }
}
